// Phase 55 — Driver Transport List
// Flattens all bookings with `transport.needed=true` into a chronological,
// driver-friendly view (one row per inbound or outbound leg). Coordinator can
// download as PDF or CSV, or email the PDF to drivers / external numbers.

import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  ArrowLeft, Car, Plane, PlaneLanding, PlaneTakeoff, Download, Mail, FileText, RefreshCw, Filter,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const todayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const addDaysIso = (iso, n) => {
  const d = new Date(iso);
  d.setDate(d.getDate() + n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const LEG_FILTERS = [
  { k: "all",      label: "Both legs",  icon: Car },
  { k: "inbound",  label: "Inbound",    icon: PlaneLanding },
  { k: "outbound", label: "Outbound",   icon: PlaneTakeoff },
];

export default function TransportList() {
  const navigate = useNavigate();
  const [from, setFrom] = useState(() => todayIso());
  const [to, setTo] = useState(() => addDaysIso(todayIso(), 30));
  const [leg, setLeg] = useState("all");
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [staff, setStaff] = useState([]);
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailDraft, setEmailDraft] = useState({ staff_ids: [], extra_recipients: "", subject: "", message: "" });
  const [emailSending, setEmailSending] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ from_date: from, to_date: to });
      if (leg !== "all") params.set("leg", leg);
      const { data } = await api.get(`/operations/transport-list?${params}`);
      setRows(data.rows || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load");
    } finally { setLoading(false); }
  }, [from, to, leg]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/housing/cleaners").then(({ data }) => setStaff(data || [])).catch(() => {});
  }, []);

  const downloadList = async (format) => {
    try {
      const params = new URLSearchParams({ from_date: from, to_date: to, format });
      if (leg !== "all") params.set("leg", leg);
      const r = await api.get(`/operations/transport-list/export?${params}`, { responseType: "blob" });
      const blob = new Blob([r.data], { type: format === "pdf" ? "application/pdf" : "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `transport_list_${leg}_${from}_to_${to}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${format.toUpperCase()} downloaded`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Download failed");
    }
    setExportMenuOpen(false);
  };

  const sendEmail = async () => {
    const extras = emailDraft.extra_recipients
      .split(/[,;\s]+/)
      .map((s) => s.trim())
      .filter((s) => s && /^[^@]+@[^@]+\.[^@]+$/.test(s));
    if (emailDraft.staff_ids.length === 0 && extras.length === 0) {
      toast.error("Pick at least one driver or add a free-text email");
      return;
    }
    setEmailSending(true);
    try {
      const { data } = await api.post("/operations/transport-list/email", {
        from_date: from,
        to_date: to,
        leg: leg === "all" ? null : leg,
        staff_ids: emailDraft.staff_ids,
        extra_recipients: extras,
        subject: emailDraft.subject || "",
        message: emailDraft.message || "",
      });
      toast.success(`Sent to ${data.sent_to.length} recipient(s) · ${data.rows} pickup(s) attached`);
      if (data.skipped?.length) {
        toast.info(`Skipped (no email on file): ${data.skipped.join(", ")}`);
      }
      setEmailOpen(false);
      setEmailDraft({ staff_ids: [], extra_recipients: "", subject: "", message: "" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Email failed");
    } finally { setEmailSending(false); }
  };

  return (
    <div className="space-y-6" data-testid="transport-list-page">
      <Button variant="ghost" onClick={() => navigate(-1)} className="text-[#7A8A84]" data-testid="back-btn">
        <ArrowLeft className="h-4 w-4 mr-2" /> Back
      </Button>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#1C1F1E] flex items-center gap-2">
            <Car className="h-6 w-6 text-[#40916C]" /> Transport List
          </h1>
          <p className="text-sm text-[#7A8A84]">Driver-ready pickup &amp; drop-off schedule, pulled from every booking with transport enabled. Edit either leg from the Bookings page.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Button variant="outline" size="sm" onClick={() => setExportMenuOpen((v) => !v)} className="border-[#1B4332]/20 text-[#1B4332]" data-testid="export-transport-btn">
              <Download className="h-3.5 w-3.5 mr-1" /> Export
            </Button>
            {exportMenuOpen && (
              <div className="absolute right-0 top-full mt-1 z-30 bg-white border border-[#E8E8E6] rounded-lg shadow-lg w-44 py-1" data-testid="transport-export-menu">
                <button onClick={() => downloadList("pdf")} className="w-full text-left text-sm px-3 py-1.5 hover:bg-[#F4F4F1] inline-flex items-center gap-2" data-testid="export-transport-pdf-btn">
                  <FileText className="h-3.5 w-3.5" /> PDF schedule
                </button>
                <button onClick={() => downloadList("csv")} className="w-full text-left text-sm px-3 py-1.5 hover:bg-[#F4F4F1] inline-flex items-center gap-2" data-testid="export-transport-csv-btn">
                  <FileText className="h-3.5 w-3.5" /> CSV (Excel)
                </button>
              </div>
            )}
          </div>
          <Button variant="outline" size="sm" onClick={() => setEmailOpen(true)} className="border-[#1B4332]/20 text-[#1B4332]" data-testid="email-transport-btn">
            <Mail className="h-3.5 w-3.5 mr-1" /> Email driver
          </Button>
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="w-40" data-testid="transport-from-date" />
          <span className="text-[#7A8A84]">→</span>
          <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="w-40" data-testid="transport-to-date" />
          <Button variant="outline" size="sm" onClick={load} data-testid="transport-refresh-btn">
            <RefreshCw className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      {/* Leg filter pills */}
      <div className="flex items-center gap-2 flex-wrap" data-testid="leg-filter-bar">
        <span className="inline-flex items-center gap-1 text-xs uppercase tracking-wider text-[#7A8A84] font-medium">
          <Filter className="h-3 w-3" /> Direction
        </span>
        {LEG_FILTERS.map((f) => {
          const Icon = f.icon;
          const on = leg === f.k;
          return (
            <button
              key={f.k}
              onClick={() => setLeg(f.k)}
              className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border transition ${
                on ? "bg-[#1B4332] text-white border-[#1B4332]" : "bg-white text-[#525C58] border-[#E8E8E6] hover:bg-[#F4F4F1]"
              }`}
              data-testid={`leg-filter-${f.k}`}
            >
              <Icon className="h-3 w-3" /> {f.label}
            </button>
          );
        })}
        <span className="ml-auto text-xs text-[#7A8A84]" data-testid="transport-result-count">
          {rows.length} pickup{rows.length === 1 ? "" : "s"}
        </span>
      </div>

      <Card className="border-[#E8E8E6]">
        <CardHeader>
          <CardTitle className="text-base">Pickups — {from} → {to}</CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          {loading ? (
            <div className="py-8 text-center text-sm text-[#7A8A84]">Loading…</div>
          ) : rows.length === 0 ? (
            <p className="py-8 text-center text-sm text-[#7A8A84]" data-testid="no-transport-rows">
              No transport pickups in this window. Tick "Transport needed" on a booking to add one.
            </p>
          ) : (
            <table className="w-full text-sm" data-testid="transport-table">
              <thead>
                <tr className="text-left border-b border-[#E8E8E6] text-xs uppercase tracking-wider text-[#7A8A84]">
                  <th className="px-2 py-2">Date</th>
                  <th className="px-2 py-2">Time</th>
                  <th className="px-2 py-2">Leg</th>
                  <th className="px-2 py-2">Visitor</th>
                  <th className="px-2 py-2">Pax</th>
                  <th className="px-2 py-2">Flight</th>
                  <th className="px-2 py-2">Pickup → Drop-off</th>
                  <th className="px-2 py-2">Phone</th>
                  <th className="px-2 py-2">Notes</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={`${r.booking_id}-${r.direction}-${r.date}`} className="border-b border-[#E8E8E6] hover:bg-[#FCFCFB]" data-testid={`transport-row-${r.booking_id}-${r.direction}`}>
                    <td className="px-2 py-2 font-medium tabular-nums">{r.date}</td>
                    <td className="px-2 py-2 tabular-nums">{r.time || "—"}</td>
                    <td className="px-2 py-2">
                      <Badge className={`${r.direction === "inbound" ? "bg-emerald-100 text-emerald-800 border border-emerald-200" : "bg-amber-100 text-amber-800 border border-amber-200"} text-[10px] inline-flex items-center gap-1`}>
                        {r.direction === "inbound" ? <PlaneLanding className="h-3 w-3" /> : <PlaneTakeoff className="h-3 w-3" />}
                        {r.direction.charAt(0).toUpperCase() + r.direction.slice(1)}
                      </Badge>
                    </td>
                    <td className="px-2 py-2 font-medium text-[#1C1F1E]">
                      <button className="hover:underline text-left" onClick={() => navigate(`/bookings/${r.booking_id}`)} data-testid={`transport-visitor-${r.booking_id}`}>{r.visitor_name}</button>
                    </td>
                    <td className="px-2 py-2 tabular-nums text-center">{r.party_size}</td>
                    <td className="px-2 py-2 font-mono text-xs">{r.flight_number || "—"}</td>
                    <td className="px-2 py-2 text-xs text-[#525C58]">
                      {[r.pickup_location, r.dropoff_location].filter(Boolean).join(" → ") || "—"}
                    </td>
                    <td className="px-2 py-2 font-mono text-xs">{r.contact_phone || "—"}</td>
                    <td className="px-2 py-2 text-xs text-[#7A8A84] max-w-[260px] truncate" title={r.leg_notes || r.transport_notes}>
                      {r.leg_notes || r.transport_notes || "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* Email dialog */}
      <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
        <DialogContent className="max-w-xl" data-testid="email-transport-dialog">
          <DialogHeader>
            <DialogTitle>Email transport list</DialogTitle>
            <DialogDescription>
              Sends the {leg === "all" ? "full" : leg} transport list for <strong>{from} → {to}</strong> as a PDF attachment via Resend.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-sm">Send to staff (drivers, dispatchers)</Label>
              <div className="flex flex-wrap gap-1.5 max-h-44 overflow-y-auto p-2 border border-[#E8E8E6] rounded-lg" data-testid="email-driver-picker">
                {(staff || []).filter((c) => c.active).map((c) => {
                  const on = emailDraft.staff_ids.includes(c.id);
                  const noEmail = !c.email;
                  return (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => {
                        const cur = new Set(emailDraft.staff_ids);
                        if (cur.has(c.id)) cur.delete(c.id); else cur.add(c.id);
                        setEmailDraft((d) => ({ ...d, staff_ids: [...cur] }));
                      }}
                      className={`px-2.5 py-1 rounded-full text-xs font-medium border transition ${
                        on ? "bg-[#1B4332] text-white border-[#1B4332]" : noEmail ? "bg-amber-50 text-amber-800 border-amber-200" : "bg-white text-[#525C58] border-[#E8E8E6] hover:bg-[#F4F4F1]"
                      }`}
                      title={noEmail ? "No email on file — will be skipped" : c.email}
                      data-testid={`email-driver-${c.id}`}
                    >
                      {c.name}{noEmail ? " ⚠️" : ""}
                    </button>
                  );
                })}
              </div>
              <p className="text-[10px] text-[#7A8A84]">Staff highlighted in amber have no email on file — they'll be skipped on send.</p>
            </div>
            <div className="space-y-2">
              <Label className="text-sm">Other emails (driver phone numbers, dispatch)</Label>
              <Textarea
                rows={2}
                placeholder="comma- or space-separated, e.g. driver1@vendor.com, fleet@hospital.org"
                value={emailDraft.extra_recipients}
                onChange={(e) => setEmailDraft((d) => ({ ...d, extra_recipients: e.target.value }))}
                data-testid="email-transport-extra-recipients"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm">Subject (optional)</Label>
              <Input
                placeholder="Defaults to: Driver Transport List — <leg> (<dates>)"
                value={emailDraft.subject}
                onChange={(e) => setEmailDraft((d) => ({ ...d, subject: e.target.value }))}
                data-testid="email-transport-subject"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm">Message (optional)</Label>
              <Textarea
                rows={3}
                placeholder="Brief note above the schedule…"
                value={emailDraft.message}
                onChange={(e) => setEmailDraft((d) => ({ ...d, message: e.target.value }))}
                data-testid="email-transport-message"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailOpen(false)}>Cancel</Button>
            <Button onClick={sendEmail} disabled={emailSending} className="bg-[#1B4332] hover:bg-[#2D6A4F]" data-testid="send-transport-email-btn">
              <Mail className="h-3.5 w-3.5 mr-1" /> {emailSending ? "Sending…" : "Send list"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
